import { Config } from '../classes/Config';
import { Log } from './Log';

export class StorageHelper {

  static keys = {
    store: "store",
    country: "country",
    mediaType: "mediaType",
    listType: "listType",
    sessionToken: "sessionToken",
    sort: "sort",
    priceType: "priceType",
    priceRange: "priceRange",
    pricePerMovieRange: "pricePerMovieRange",
    quality: "quality",
    genre: "genre",
    shortWishlist: "shortWishlist",
    shortBlocklist: "shortBlocklist",
    shortOwnlist: "shortOwnlist",
    username: "username",
    profileImageUrl: "profileImageUrl",
    detailViewMediaType: "detailViewMediaType",
    wishlistCelebrationPopupFlag: "wishlistCelebrationPopupFlag",
    device: "device",
    advisoryRating: "advisoryRating",
    cookiesAccepted: "cookiesAccepted",
    moviesAnywhere: "moviesAnywhere",
    hdrFormat: "hdrFormat",
    movieRatingsFilters : "movieRatingsFilters",
    downScreenMessage: "downScreenMessage",
    lang: "lang",
    sound: "sound",
    filterOpen: "filterOpen",
    uniqueCustomerId: "uniqueCustomerId",
    newsPopup: "newsPopup",
    studio: "studio",
    email: "email",
    searchItemType: "searchItemType",
    darkTheme: "darkTheme",
    newCardDeal: "newCardDeal",
    lastCardDealId: "lastCardDealId",
    buyRent: "buyRent",
    frontendToken: "frontendToken",
    audiotrack: "audiotrack",
    itunesExtras: "itunesExtras",
    offersFilter: "offerFilter",
    releaseYear: "releaseYear",
    runtime: "runtime",
    hideFrenchTitles: "hideFrenchTitles",
    inviteCode: "inviteCode",
    inviteCodeTime: "inviteCodeTime",
    visit: "visit",
    sortPeriod: "sortPeriod",
    showSortPeriodNewFeatureBubble: "showSortPeriodNewFeatureBubble",
    showImportMacOSNewFeatureBubble: "showImportMacOSNewFeatureBubble",
    showHideFrenchTitlesNewFeaturePopup: "showHideFrenchTitlesNewFeaturePopup",
    showBundles: "showBundles",
    visionPro3d: "visionPro3d",
    ignoreSdPriceOnWishlist: "ignoreSdPriceOnWishlist",
    themeMode: "themeMode"
  };

  static LOCAL_STORAGE = "localStorage";
  static SESSION_STORAGE = "sessionStorage";
  static COOKIES = "cookies";

  //Singleton
  private constructor() {

    if (this.isLocalStorageSupported()) {
      this.usedStorage = StorageHelper.LOCAL_STORAGE;
    }
    else if (this.isSessionStorageSupported()) {
      this.usedStorage = StorageHelper.SESSION_STORAGE;
    }
    else
    {
      this.usedStorage = StorageHelper.COOKIES;
    }

  }
  private usedStorage;
  private static instance = new StorageHelper();
  static getInstance(): StorageHelper {
    return this.instance;
  }

  private isLocalStorageSupported(): boolean
  {
      try
      {
          localStorage.setItem("test", '1');
          localStorage.removeItem("test");
          return true;
      }
      catch (error)
      {
          return false;
      }
  }

  private isSessionStorageSupported(): boolean{
    try
      {
          sessionStorage.setItem("test", '1');
          sessionStorage.removeItem("test");
          return true;
      }
      catch (error)
      {
          return false;
      }
  }

  public save(key: string, value: any): boolean {

    if (key && value !== null && value !== undefined){
      try {
        const stringifiedValue = JSON.stringify(value);

        // Check size for critical lists before attempting to save
        if (key === StorageHelper.keys.shortBlocklist ||
          key === StorageHelper.keys.shortWishlist ||
          key === StorageHelper.keys.shortOwnlist) {
          const sizeInBytes = new Blob([stringifiedValue]).size;
          const sizeInMB = sizeInBytes / (1024 * 1024);
          const sizeInKB = sizeInBytes / 1024;

          // Log size for all critical list saves
          Log.getInstance().d(`StorageHelper.save: Saving ${key} - Size: ${sizeInKB.toFixed(2)} KB (${sizeInMB.toFixed(3)} MB)`);

          // Calculate item count for better logging
          let itemCount = 0;
          if (value && typeof value === 'object') {
            try {
              // Count items across all stores and media types
              for (const store in value) {
                if (value[store] && typeof value[store] === 'object') {
                  for (const mediaType in value[store]) {
                    if (Array.isArray(value[store][mediaType])) {
                      itemCount += value[store][mediaType].length;
                    }
                  }
                }
              }
              Log.getInstance().d(`StorageHelper.save: ${key} contains ${itemCount} items across all stores/media types`);
            } catch (e) {
              // Ignore counting errors
            }
          }

          // Warn if approaching limit (4 MB is a safe threshold)
          if (sizeInMB > 4) {
            Log.getInstance().d(`StorageHelper.save: WARNING - ${key} is very large (${sizeInMB.toFixed(2)} MB). This may cause storage issues.`);
            if (typeof console !== 'undefined' && console.warn) {
              console.warn(`StorageHelper.save: ${key} is very large (${sizeInMB.toFixed(2)} MB). This may cause storage issues.`);
            }
          }

          // If size is extremely large (>4.5 MB), try to clear cache first
          if (sizeInMB > 4.5) {
            Log.getInstance().d(`StorageHelper.save: ${key} exceeds 4.5 MB (${sizeInMB.toFixed(2)} MB). Attempting to clear cache before save.`);
            this.handleStorageQuotaExceeded(key, value);
          }
        }

        if (this.usedStorage == StorageHelper.LOCAL_STORAGE) {
          localStorage.setItem(key, stringifiedValue);
          return true;
        }

        else if (this.usedStorage == StorageHelper.SESSION_STORAGE) {
          sessionStorage.setItem(key, stringifiedValue);
          return true;
        }

        else if (this.usedStorage == StorageHelper.COOKIES) {
          this.createCookie(key, value);
          return true;
        }
      } catch (error) {
        // Handle QuotaExceededError and other storage errors
        if (error.name === 'QuotaExceededError' || error.code === 22 || error.code === 1014) {
          const sizeInBytes = new Blob([JSON.stringify(value)]).size;
          const sizeInMB = sizeInBytes / (1024 * 1024);

          Log.getInstance().d(`StorageHelper.save: QuotaExceededError for ${key} (${sizeInMB.toFixed(2)} MB). Attempting recovery...`);

          // Storage is full, try to clear old data for blocklist/wishlist/ownlist
          if (key === StorageHelper.keys.shortBlocklist ||
            key === StorageHelper.keys.shortWishlist ||
            key === StorageHelper.keys.shortOwnlist) {
            // Try to clear cache and retry once
            const cleared = this.handleStorageQuotaExceeded(key, value);
            if (cleared) {
              Log.getInstance().d(`StorageHelper.save: Cache cleared. Retrying save for ${key}...`);
              // Retry saving after clearing cache
              try {
                const stringifiedValue = JSON.stringify(value);
                if (this.usedStorage == StorageHelper.LOCAL_STORAGE) {
                  localStorage.setItem(key, stringifiedValue);
                  Log.getInstance().d(`StorageHelper.save: Retry successful for ${key}`);
                  return true;
                } else if (this.usedStorage == StorageHelper.SESSION_STORAGE) {
                  sessionStorage.setItem(key, stringifiedValue);
                  Log.getInstance().d(`StorageHelper.save: Retry successful for ${key}`);
                  return true;
                }
              } catch (retryError) {
                // If retry also fails, log and return false
                Log.getInstance().d(`StorageHelper.save: Retry FAILED for ${key} after cache clear. Error: ${retryError.message || retryError}`);
                if (typeof console !== 'undefined' && console.error) {
                  console.error('StorageHelper.save: Retry after cache clear also failed for key:', key, retryError);
                }
              }
            } else {
              Log.getInstance().d(`StorageHelper.save: Could not clear enough cache for ${key}. Save failed.`);
            }
          }
        }
        // Log error but don't throw to prevent app crash
        Log.getInstance().d(`StorageHelper.save: FAILED for key ${key}. Error: ${error.name || error.code || error.message || error}`);
        if (typeof console !== 'undefined' && console.error) {
          console.error('StorageHelper.save failed for key:', key, error);
        }
        return false;
      }
    }
    return false;
  }

  private handleStorageQuotaExceeded(key: string, value: any): boolean {
    // Try to clear cache entries to free up space
    try {
      let clearedCount = 0;
      let totalFreedBytes = 0;

      // Log current storage usage before clearing
      const usageBefore = this.getTotalStorageUsage();
      Log.getInstance().d(`StorageHelper.handleStorageQuotaExceeded: Current storage usage before clearing: ${usageBefore.formatted}`);

      // Clear old cache entries (assuming cache keys have a prefix)
      const cacheKeys = [];
      if (this.usedStorage == StorageHelper.LOCAL_STORAGE) {
        for (let i = 0; i < localStorage.length; i++) {
          const cacheKey = localStorage.key(i);
          // Clear cache entries but keep important data
          if (cacheKey && cacheKey.startsWith('cache_') && !cacheKey.includes('blocklist') &&
            !cacheKey.includes('wishlist') && !cacheKey.includes('ownlist')) {
            cacheKeys.push(cacheKey);
          }
        }

        Log.getInstance().d(`StorageHelper.handleStorageQuotaExceeded: Found ${cacheKeys.length} cache entries to potentially clear`);

        // Remove cache entries to free space (remove more aggressively)
        const entriesToRemove = Math.min(cacheKeys.length, 50);
        for (let i = 0; i < entriesToRemove; i++) {
          try {
            const cacheKey = cacheKeys[i];
            const sizeBefore = this.getStorageSize(cacheKey);
            localStorage.removeItem(cacheKey);
            totalFreedBytes += sizeBefore;
            clearedCount++;
            Log.getInstance().d(`StorageHelper.handleStorageQuotaExceeded: Cleared cache entry: ${cacheKey} (${(sizeBefore / 1024).toFixed(2)} KB)`);
          } catch (e) {
            // Continue removing other entries even if one fails
            Log.getInstance().d(`StorageHelper.handleStorageQuotaExceeded: Failed to clear cache entry ${cacheKeys[i]}: ${e.message || e}`);
          }
        }

        // Also try to remove old filter entries (they're less critical)
        const filterKeys = [];
        for (let i = 0; i < localStorage.length; i++) {
          const storageKey = localStorage.key(i);
          // Remove old filter entries but keep current ones
          if (storageKey && storageKey.includes('_') && !storageKey.includes('shortBlocklist') &&
            !storageKey.includes('shortWishlist') && !storageKey.includes('shortOwnlist') &&
            !storageKey.includes('sessionToken') && !storageKey.includes('username')) {
            filterKeys.push(storageKey);
          }
        }

        Log.getInstance().d(`StorageHelper.handleStorageQuotaExceeded: Found ${filterKeys.length} filter entries to potentially clear`);

        // Remove up to 20 filter entries
        const filtersToRemove = Math.min(filterKeys.length, 20);
        for (let i = 0; i < filtersToRemove; i++) {
          try {
            const filterKey = filterKeys[i];
            const sizeBefore = this.getStorageSize(filterKey);
            localStorage.removeItem(filterKey);
            totalFreedBytes += sizeBefore;
            clearedCount++;
            Log.getInstance().d(`StorageHelper.handleStorageQuotaExceeded: Cleared filter entry: ${filterKey} (${(sizeBefore / 1024).toFixed(2)} KB)`);
          } catch (e) {
            // Continue removing other entries even if one fails
            Log.getInstance().d(`StorageHelper.handleStorageQuotaExceeded: Failed to clear filter entry ${filterKeys[i]}: ${e.message || e}`);
          }
        }

        // Log final results
        const usageAfter = this.getTotalStorageUsage();
        const freedMB = totalFreedBytes / (1024 * 1024);
        Log.getInstance().d(`StorageHelper.handleStorageQuotaExceeded: Cleared ${clearedCount} entries, freed ${freedMB.toFixed(2)} MB. Storage usage: ${usageBefore.formatted} -> ${usageAfter.formatted}`);

        if (clearedCount > 0 && typeof console !== 'undefined' && console.log) {
          console.log(`StorageHelper: Cleared ${clearedCount} cache/filter entries, freed ${freedMB.toFixed(2)} MB`);
        }

        return clearedCount > 0;
      }
    } catch (e) {
      // If clearing cache also fails, just log it
      Log.getInstance().d(`StorageHelper.handleStorageQuotaExceeded: FAILED to clear cache. Error: ${e.message || e}`);
      if (typeof console !== 'undefined' && console.error) {
        console.error('Failed to clear cache for quota:', e);
      }
    }
    return false;
  }

  /**
   * Get the size of a value in bytes when stringified
   */
  public getStorageSize(key: string): number {
    const value = this.get(key);
    if (value === null || value === undefined) {
      return 0;
    }
    try {
      const stringified = JSON.stringify(value);
      return new Blob([stringified]).size;
    } catch (e) {
      return 0;
    }
  }

  /**
   * Get the size of a value in a human-readable format
   */
  public getStorageSizeFormatted(key: string): string {
    const sizeInBytes = this.getStorageSize(key);
    if (sizeInBytes < 1024) {
      return sizeInBytes + ' B';
    } else if (sizeInBytes < 1024 * 1024) {
      return (sizeInBytes / 1024).toFixed(2) + ' KB';
    } else {
      return (sizeInBytes / (1024 * 1024)).toFixed(2) + ' MB';
    }
  }

  /**
   * Get total storage usage for the current storage type
   */
  public getTotalStorageUsage(): { bytes: number, formatted: string } {
    let totalBytes = 0;

    try {
      if (this.usedStorage == StorageHelper.LOCAL_STORAGE) {
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key) {
            const value = localStorage.getItem(key);
            if (value) {
              totalBytes += new Blob([value]).size;
              // Also account for key size
              totalBytes += new Blob([key]).size;
            }
          }
        }
      } else if (this.usedStorage == StorageHelper.SESSION_STORAGE) {
        for (let i = 0; i < sessionStorage.length; i++) {
          const key = sessionStorage.key(i);
          if (key) {
            const value = sessionStorage.getItem(key);
            if (value) {
              totalBytes += new Blob([value]).size;
              totalBytes += new Blob([key]).size;
            }
          }
        }
      }
    } catch (e) {
      Log.getInstance().d(`StorageHelper.getTotalStorageUsage: Error calculating usage: ${e.message || e}`);
    }

    let formatted: string;
    if (totalBytes < 1024) {
      formatted = totalBytes + ' B';
    } else if (totalBytes < 1024 * 1024) {
      formatted = (totalBytes / 1024).toFixed(2) + ' KB';
    } else {
      formatted = (totalBytes / (1024 * 1024)).toFixed(2) + ' MB';
    }

    return { bytes: totalBytes, formatted: formatted };
  }

  /**
   * Log detailed storage usage breakdown (useful for debugging)
   */
  public logStorageUsage() {
    const usage = this.getTotalStorageUsage();
    Log.getInstance().d(`StorageHelper.logStorageUsage: Total storage usage: ${usage.formatted}`);

    // Log sizes of critical lists
    const criticalKeys = [
      StorageHelper.keys.shortBlocklist,
      StorageHelper.keys.shortWishlist,
      StorageHelper.keys.shortOwnlist
    ];

    for (const key of criticalKeys) {
      const size = this.getStorageSizeFormatted(key);
      if (parseFloat(size) > 0) {
        Log.getInstance().d(`StorageHelper.logStorageUsage: ${key}: ${size}`);

        // Log item count for critical lists
        const value = this.get(key);
        if (value && typeof value === 'object') {
          let itemCount = 0;
          try {
            for (const store in value) {
              if (value[store] && typeof value[store] === 'object') {
                for (const mediaType in value[store]) {
                  if (Array.isArray(value[store][mediaType])) {
                    itemCount += value[store][mediaType].length;
                  }
                }
              }
            }
            Log.getInstance().d(`StorageHelper.logStorageUsage: ${key} contains ${itemCount} items`);
          } catch (e) {
            // Ignore counting errors
          }
        }
      }
    }

    // Log number of total entries
    let entryCount = 0;
    try {
      if (this.usedStorage == StorageHelper.LOCAL_STORAGE) {
        entryCount = localStorage.length;
      } else if (this.usedStorage == StorageHelper.SESSION_STORAGE) {
        entryCount = sessionStorage.length;
      }
      Log.getInstance().d(`StorageHelper.logStorageUsage: Total storage entries: ${entryCount}`);
    } catch (e) {
  // Ignore errors
    }
  }

  public saveFilter(store: string, mediaType: string, listType: string, filterKey: string, value: any) {
    const key = store + '_' + mediaType + '_' + listType + '_' + filterKey;
    return this.save(key, value);
  }

  public getFilter(store: string, mediaType: string, listType: string, filterKey: string): any {
    const key = store + '_' + mediaType + '_' + listType + '_' + filterKey;
    return this.get(key);
  }

  public get(key: string): any {
    if (key == null) {
      return "";
    }

    let value = null;
    try {
      if (this.usedStorage == StorageHelper.LOCAL_STORAGE) {
        value = localStorage.getItem(key);
      }
      else if (this.usedStorage == StorageHelper.SESSION_STORAGE) {
        value = sessionStorage.getItem(key);
      }
      else if (this.usedStorage == StorageHelper.COOKIES) {
        value = this.readCookie(key);
      }

      //Parse the value
      if (value !== null && value !== undefined) {
        try {
          value = JSON.parse(value);
        } catch (e) {
          // If JSON parsing fails, the value might be corrupted
          // For critical data like blocklist, return null to force reload
          if (key === StorageHelper.keys.shortBlocklist ||
            key === StorageHelper.keys.shortWishlist ||
            key === StorageHelper.keys.shortOwnlist) {
            if (typeof console !== 'undefined' && console.error) {
              console.error('StorageHelper.get: Failed to parse critical data for key:', key, e);
            }
            // Clear corrupted data
            this.clear(key);
            return null;
          }
          // For other data, return as string if parsing fails
          return value;
        }
      }
    } catch (error) {
      // Handle any storage access errors
      if (typeof console !== 'undefined' && console.error) {
        console.error('StorageHelper.get failed for key:', key, error);
      }
      return null;
    }

    return value;
  }

  public clear(key: string) {
    if (key){
      if (this.usedStorage == StorageHelper.LOCAL_STORAGE){
        localStorage.removeItem(key);
      }
      else if (this.usedStorage == StorageHelper.SESSION_STORAGE){
        sessionStorage.removeItem(key);
      }
      else {
        //Deleting cookie by settings expiry date to a passed date
        this.deleteCookie(key);
      }
    }
  }

  private createCookie(key: string, value: any) {
    //Create expiration date
    const d = new Date();
    const year = d.getFullYear();
    const month = d.getMonth();
    const day = d.getDate();
    const c = new Date(year + 20, month, day);

    document.cookie = key + "=" + JSON.stringify(value) + "; expires=" + c.toString() + "; path=/";
  }

  private readCookie(key: string) {
    const nameEQ = key + "=";
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) == ' ') c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) == 0) return c.substring(nameEQ.length, c.length);
    }
    return null;
  }

  private readCookieAtIndex(index: number) {
    const ca = document.cookie.split(';');
    return ca[index];
  }

  private deleteCookie(key: string) {
    document.cookie = key + "=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
  }

  public removeAllWithPrefix(prefix: string) {
    let storageLength = document.cookie.split(';').length;
    if(this.usedStorage == StorageHelper.LOCAL_STORAGE) {
      storageLength = localStorage.length;
    }
    else if(this.usedStorage == StorageHelper.SESSION_STORAGE) {
      storageLength = sessionStorage.length;
    }
    const arr = []; // Array to hold the keys
    // Iterate over localStorage and insert the keys that meet the condition into arr
    for (let i = 0; i < storageLength; i++) {

      if (this.usedStorage == StorageHelper.LOCAL_STORAGE) {

        if (localStorage.key(i).substring(0,prefix.length) == prefix) {
          arr.push(localStorage.key(i));
        }

      }
      else if (this.usedStorage == StorageHelper.SESSION_STORAGE) {

        if (sessionStorage.key(i).substring(0,prefix.length) == prefix) {
          arr.push(sessionStorage.key(i));
        }

      }
      else {
        let cookieAtIndex = this.readCookieAtIndex(i);
        if (cookieAtIndex.substring(0,prefix.length) == prefix) {
          arr.push(cookieAtIndex);
        }

      }

    }

    // Iterate over arr and remove the items by key
    for (let i = 0; i < arr.length; i++) {
      this.clear(arr[i]);
    }
  }
}
